import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Output,
  ViewChild,
  viewChild,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import Keycloak from 'keycloak-js';
import { DropDownButtonModule } from '@syncfusion/ej2-angular-splitbuttons';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';

import {
  I18N,
  OrganizationConfigService,
  TranslatePipe,
  PersonNameCasePipe,
  formatPhoneWithPrefix,
} from 'O3_core';
import { ContextMenuService, ModalComponent } from 'O3_structure';
import { environment } from '../../environments/environment';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { PatientSearchGlobalService } from 'O3_patient-management';
import type { IPatientSearchGlobalItem } from 'O3_patient-management';
import {
  MessagingOrchestratorService,
  MessagingSlideIn,
} from 'O3_communication';
import { TicketCaptureWidget } from 'O3_support';

@Component({
  selector: 'o3-core-app-topbar',
  imports: [
    RouterLink,
    TranslatePipe,
    PersonNameCasePipe,
    DropDownButtonModule,
    ButtonModule,
    ModalComponent,
    ReactiveFormsModule,
    MessagingSlideIn,
    TicketCaptureWidget,
  ],
  templateUrl: './app-topbar.html',
  styleUrl: './app-topbar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTopbar {
  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() toggleNotificationsbar = new EventEmitter<void>();

  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private keycloak = inject(Keycloak);
  private contextMenuService = inject(ContextMenuService);
  private i18n = inject(I18N);
  private organizationConfig = inject(OrganizationConfigService);
  private patientSearchGlobal = inject(PatientSearchGlobalService);
  private messagingOrchestrator = inject(MessagingOrchestratorService);

  // Derived from the profile so initials track the authenticated user once
  // Keycloak loads it (no stale initials or '??' placeholder).
  userInitials = computed<string>(() => {
    const profile = this.contextMenuService.userProfile();
    if (profile?.firstName && profile?.lastName) {
      return (profile.firstName[0] + profile.lastName[0]).toUpperCase();
    }
    if (profile?.username) {
      return profile.username.substring(0, 2).toUpperCase();
    }
    return '??';
  });
  userFullName = computed<string>(() => {
    const profile = this.contextMenuService.userProfile();
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName} ${profile.lastName}`;
    }
    return profile?.username ?? '';
  });
  userEmail = computed<string>(
    () => this.contextMenuService.userProfile()?.email ?? '',
  );
  userMenuOpen = signal(false);
  isFullscreen = signal(false);
  isMessagingSlideInOpen = signal(false);
  isTicketCaptureOpen = signal(false);

  totalUnread = this.messagingOrchestrator.totalUnread;

  // dropdown state
  searchOpen = signal(false);
  @ViewChild('searchWrap', { static: false })
  searchWrap?: ElementRef<HTMLElement>;
  @ViewChild('searchInput', { static: false })
  searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('userMenuWrap', { static: false })
  userMenuWrap?: ElementRef<HTMLElement>;

  searchCtrl = new FormControl<string>('', { nonNullable: true });
  searchLoading = signal(false);
  searchError = signal<string | null>(null);
  searchItems = signal<IPatientSearchGlobalItem[]>([]);
  searchTotal = signal<number>(0);

  modalRef = viewChild<ModalComponent>('contextMenuModal');

  // Guards the one-shot context prompt so it fires at most once per session.
  private contextPromptEvaluated = false;

  constructor() {
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen.set(!!document.fullscreenElement);
    });

    // Prompt for company/location once the profile and modal are ready, so the
    // context check runs after the user id is resolved (not before).
    effect(() => {
      if (this.contextPromptEvaluated) return;

      const profile = this.contextMenuService.userProfile();
      const modal = this.modalRef();
      if (!profile || !modal) return;

      // Profile and view are ready: decide once and stop reacting.
      this.contextPromptEvaluated = true;
      if (this.contextMenuService.getContextData() == null) {
        this.showContextMenuModal();
      }
    });

    this.searchCtrl.valueChanges
      .pipe(
        map((v) => v.trim()),
        debounceTime(500),
        distinctUntilChanged(),
        tap((q) => {
          this.searchError.set(null);

          if (q.length < 2) {
            this.searchLoading.set(false);
            this.searchItems.set([]);
            this.searchTotal.set(0);
            return;
          }

          // open while searching (even before results arrive)
          this.searchOpen.set(true);
        }),
        filter((q) => q.length >= 2),
        tap(() => this.searchLoading.set(true)),
        switchMap((q) =>
          this.patientSearchGlobal.search(q, 1, 5).pipe(
            catchError(() => {
              this.searchError.set('Search failed');
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((page) => {
        this.searchLoading.set(false);

        const items = page?.items ?? [];

        const transformed = items.map((p) => ({
          ...p,
          phone: this.formatPhone(p.phone ?? '', p.phonePrefix?.indicative),
          cnp: p.cnp ?? '',
        })) as IPatientSearchGlobalItem[];

        this.searchItems.set(transformed);
        this.searchTotal.set(page?.total ?? 0);
      });
  }
  // close dropdowns on outside click (correct place: class method)
  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent) {
    const target = e.target as Node | null;
    if (!target) return;

    const searchWrap = this.searchWrap?.nativeElement;
    if (searchWrap && !searchWrap.contains(target)) {
      this.searchOpen.set(false);
    }

    const userMenuWrap = this.userMenuWrap?.nativeElement;
    if (userMenuWrap && !userMenuWrap.contains(target)) {
      this.userMenuOpen.set(false);
    }
  }

  // Ctrl+K (and Cmd+K on mac) focuses the search input
  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(e: KeyboardEvent) {
    const isK = (e.key || '').toLowerCase() === 'k';
    const withCtrlOrCmd = e.ctrlKey || e.metaKey;

    if (!withCtrlOrCmd || !isK) return;

    e.preventDefault();
    e.stopPropagation();

    const input = this.searchInput?.nativeElement;
    if (!input) return;

    input.focus();
    input.select();

    this.onSearchFocus();
  }

  onSearchFocus() {
    this.searchOpen.set(true);
  }

  clearSearch() {
    this.searchCtrl.setValue('');
    this.searchOpen.set(false);
  }

  onSelectPatient(p: IPatientSearchGlobalItem) {
    this.searchOpen.set(false);
    this.router.navigate([
      '/patient-management',
      'patient-record',
      p.id,
      'overview',
    ]);
  }

  goToNewPatient() {
    this.searchOpen.set(false);
    this.router.navigate(['/patient-management', 'patient', 'form']);
  }

  private formatPhone(phone: string, prefix?: string | null): string {
    return formatPhoneWithPrefix(prefix, phone);
  }

  toggleUserMenu(): void {
    this.userMenuOpen.update((v) => !v);
  }

  logout(): void {
    this.contextMenuService.clearUserProfile();
    this.keycloak.logout();
  }

  get companyName(): string | null {
    return this.contextMenuService.companyName();
  }

  get locationName(): string | null {
    return this.contextMenuService.locationName();
  }

  get selectedLanguage(): string {
    return this.i18n.getLanguage();
  }

  get items() {
    return this.i18n.languageList;
  }

  get isDevelopment(): boolean {
    return environment.environmentName === 'development';
  }

  handleMenuItemClick($event: { item: { text: string } }) {
    const language = this.i18n.getLnaguageByText($event.item.text)?.code;
    if (language) {
      this.i18n.changeLanguage(language);
      window.location.reload();
    }
  }

  handleSidebarToggle() {
    this.toggleSidebar.emit();
  }

  handleNotificationsbarToggle() {
    this.toggleNotificationsbar.emit();
  }

  showContextMenuModal() {
    this.modalRef()?.showModal();
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  toggleMessagingSlideIn(): void {
    this.isMessagingSlideInOpen.update((v) => !v);
  }

  closeMessagingSlideIn(): void {
    this.isMessagingSlideInOpen.set(false);
  }

  onSlideInConversationOpened(conversationId: string): void {
    this.isMessagingSlideInOpen.set(false);
    this.messagingOrchestrator.openFloatingChat(conversationId);
  }

  toggleTicketCapture(): void {
    // Only one right-hand slide-over at a time — they occupy the same space.
    this.isMessagingSlideInOpen.set(false);
    this.isTicketCaptureOpen.update((v) => !v);
  }

  closeTicketCapture(): void {
    this.isTicketCaptureOpen.set(false);
  }
}
